<script setup lang="ts">
// The Details rail's multi-selection view. It is what the ledger's ticks are
// for: when more than one row is ticked, this replaces the single-record popup
// with the count, the one type selected, and the writes that type supports —
// assignment and tags on Sites, the planned revisit on Features. A single
// ticked row keeps the record's own detail; this panel starts at two.
//
// Every write is narrowed to `store.bulkSelection`: the spec still says which
// rows the table is showing, and the selection says which of them are ticked. So
// an operator reads the count here and writes to exactly those rows, with the
// help text beside each control stating what it supersedes or clears.
import { computed, onMounted, ref } from "vue";

import ButtonLoading from "@/components/common/ButtonLoading.vue";
import { formatBigNumber } from "@/format";
import { loadSymbologyCatalog, symbologyCatalog } from "@/lib/symbologyCatalog";
import { useStoreMapReview } from "@/store/map-review";
import type { SymbolTagStyle } from "@/type/api";

import {
	MAP_REVIEW_TABLE_LABELS,
	MAP_REVIEW_TABLE_SINGULAR,
	columnByKey,
	localDayString,
	revisitDateToInstant,
} from "./columns";
import { useColumnOptions } from "./useColumnOptions";

const store = useStoreMapReview();
const { optionsFor } = useColumnOptions();

const tableLabel = computed(() => MAP_REVIEW_TABLE_LABELS[store.activeTable]);
const tableSingular = computed(
	() => MAP_REVIEW_TABLE_SINGULAR[store.activeTable],
);
const count = computed(() => store.ledgerSelectionCount);
const selection = computed(() => store.ledgerSelection);

/** What the selection covers, in the operator's terms: every row matching the
 * filter (with its unticked remainder), or an explicit set of ticks. */
const scope = computed(() => {
	if (!selection.value.all) {
		return `${formatBigNumber(count.value)} ticked here, and on any other page of this table.`;
	}
	const total = formatBigNumber(store.total);
	if (selection.value.excluded.length === 0) {
		return `Every ${tableSingular.value} matching the current filter: ${total}.`;
	}
	return `${formatBigNumber(count.value)} of the ${total} ${tableLabel.value} matching the current filter; the ${formatBigNumber(selection.value.excluded.length)} unticked are excluded.`;
});

// --- Assignment (Sites) -----------------------------------------------------

const assignKind = ref("inspect");
const assignTechnician = ref<number | null>(null);
const assignDate = ref(localDayString(new Date()));

const technicianOptions = computed(() =>
	optionsFor(columnByKey("site", "assigned_to")!),
);

// --- Tags (Sites) -----------------------------------------------------------

const tagAdd = ref<number | null>(null);
const tagRemove = ref<number | null>(null);

const liveTags = computed<SymbolTagStyle[]>(() =>
	(symbologyCatalog.value?.tags ?? []).filter((tag) => !tag.retired),
);
const catalogTags = computed<SymbolTagStyle[]>(
	() => symbologyCatalog.value?.tags ?? [],
);

// --- Planned revisit (Features) ---------------------------------------------

const revisitDraft = ref("");

// --- Shared write state -----------------------------------------------------

const busy = ref<string | null>(null);
const actionError = ref("");
const actionStatus = ref("");

/** The selection as one phrase, used inside every confirmation: what the write
 * will touch, stated the same way for all three actions. */
function targetPhrase(): string {
	if (!selection.value.all) {
		return `the ${formatBigNumber(count.value)} ticked ${tableLabel.value}`;
	}
	if (selection.value.excluded.length > 0) {
		return `${formatBigNumber(count.value)} of the ${formatBigNumber(store.total)} ${tableLabel.value} matching the current filter (the ${formatBigNumber(selection.value.excluded.length)} unticked are left alone)`;
	}
	return `all ${formatBigNumber(count.value)} ${tableLabel.value} matching the current filter`;
}

function failure(message: string): void {
	actionError.value = store.error || message;
}

async function assignSites(): Promise<void> {
	if (busy.value) return;
	if (assignTechnician.value === null || assignDate.value === "") {
		actionError.value = "Choose a technician and a day first.";
		return;
	}
	const technician = technicianOptions.value.find(
		(option) => option.value === assignTechnician.value,
	);
	if (
		!confirm(
			[
				`Assign ${technician?.label ?? "the chosen technician"}, on ${assignDate.value}, to ${targetPhrase()}?`,
				"",
				"Each Site ends up assigned to this one technician, replacing the assignment it has now — a Site reads as assigned to one person at a time. The earlier plan stays in the history. Sites outside this selection keep their own assignment.",
				"The map is already showing the selection, so the map is the preview.",
			].join("\n"),
		)
	) {
		return;
	}
	busy.value = "assign";
	actionError.value = "";
	actionStatus.value = "";
	try {
		const sites = await store.assignSites(
			store.bulkSelection,
			assignTechnician.value,
			assignDate.value,
			assignKind.value,
		);
		actionStatus.value = `Assigned ${formatBigNumber(sites)} ${tableLabel.value}.`;
	} catch {
		failure("The assignment could not be written.");
	} finally {
		busy.value = null;
	}
}

async function clearAssignments(): Promise<void> {
	if (busy.value) return;
	if (
		!confirm(
			[
				`Clear the assignment of ${targetPhrase()}?`,
				"",
				"Each of those Sites is left with no one assigned. Any Site outside this selection keeps its own assignment. The cancelled plans stay in the history.",
			].join("\n"),
		)
	) {
		return;
	}
	busy.value = "clear-assignment";
	actionError.value = "";
	actionStatus.value = "";
	try {
		const sites = await store.clearSiteAssignments(store.bulkSelection);
		actionStatus.value = `Cleared the assignment of ${formatBigNumber(sites)} ${tableLabel.value}.`;
	} catch {
		failure("The assignment could not be cleared.");
	} finally {
		busy.value = null;
	}
}

async function applyTags(): Promise<void> {
	if (busy.value) return;
	const add = tagAdd.value === null ? [] : [tagAdd.value];
	const remove = tagRemove.value === null ? [] : [tagRemove.value];
	if (add.length === 0 && remove.length === 0) {
		actionError.value = "Choose a tag to apply, a tag to remove, or both.";
		return;
	}
	if (tagAdd.value !== null && tagAdd.value === tagRemove.value) {
		actionError.value = "A tag cannot be both applied and removed.";
		return;
	}
	const addName = liveTags.value.find(
		(tag) => tag.tag_id === tagAdd.value,
	)?.name;
	const removeName = catalogTags.value.find(
		(tag) => tag.tag_id === tagRemove.value,
	)?.name;
	const lines: string[] = [];
	if (addName && removeName) {
		lines.push(
			`Apply “${addName}” and remove “${removeName}” on ${targetPhrase()}?`,
		);
	} else if (addName) {
		lines.push(`Apply “${addName}” to ${targetPhrase()}?`);
	} else {
		lines.push(`Remove “${removeName}” from ${targetPhrase()}?`);
	}
	lines.push("");
	lines.push(
		"A Site may carry at most 12 tags. If this would push any Site past that, the whole batch is refused and those Sites are named, so nothing is written half-way.",
	);
	lines.push(
		"The highest-priority tag with a district symbol bound to it styles the map; a Site with no winning tag keeps its technician colour.",
	);
	if (!confirm(lines.join("\n"))) return;

	busy.value = "tags";
	actionError.value = "";
	actionStatus.value = "";
	try {
		const result = await store.assignSiteTags(store.bulkSelection, add, remove);
		// A retired tag can drop out of the catalog once its last live
		// assignment goes, so re-read it rather than leaving the picker naming
		// a tag no Site carries.
		void loadSymbologyCatalog(true).catch(() => {});
		tagAdd.value = null;
		tagRemove.value = null;
		const parts: string[] = [];
		if (result.assigned > 0) {
			parts.push(
				`applied ${formatBigNumber(result.assigned)} tag assignment${result.assigned === 1 ? "" : "s"}`,
			);
		}
		if (result.removed > 0) {
			parts.push(
				`removed ${formatBigNumber(result.removed)} tag assignment${result.removed === 1 ? "" : "s"}`,
			);
		}
		actionStatus.value =
			parts.length === 0
				? `No change across ${formatBigNumber(result.sites)} Sites.`
				: `Tags: ${parts.join(", ")} across ${formatBigNumber(result.sites)} Sites.`;
	} catch {
		// The cap refusal names the Sites; the store keeps that message and the
		// view toasts it, and it is shown here beside the controls too.
		failure("The tags could not be written.");
	} finally {
		busy.value = null;
	}
}

async function setRevisit(): Promise<void> {
	if (busy.value) return;
	if (revisitDraft.value === "") {
		actionError.value = "Choose a revisit date first.";
		return;
	}
	if (
		!confirm(
			`Set the planned revisit date to ${revisitDraft.value} on ${targetPhrase()}?`,
		)
	) {
		return;
	}
	busy.value = "revisit";
	actionError.value = "";
	actionStatus.value = "";
	try {
		const changed = await store.setPlannedRevisit(
			store.bulkSelection,
			revisitDateToInstant(revisitDraft.value),
		);
		actionStatus.value = `Set the revisit date on ${formatBigNumber(changed)} ${tableLabel.value}.`;
		revisitDraft.value = "";
	} catch {
		failure("The revisit date could not be saved.");
	} finally {
		busy.value = null;
	}
}

async function clearRevisit(): Promise<void> {
	if (busy.value) return;
	if (!confirm(`Clear the planned revisit date on ${targetPhrase()}?`)) {
		return;
	}
	busy.value = "clear-revisit";
	actionError.value = "";
	actionStatus.value = "";
	try {
		const changed = await store.setPlannedRevisit(store.bulkSelection, null);
		actionStatus.value = `Cleared the revisit date on ${formatBigNumber(changed)} ${tableLabel.value}.`;
	} catch {
		failure("The revisit date could not be cleared.");
	} finally {
		busy.value = null;
	}
}

// --- VectorSurv filing capture (Treatments) ---------------------------------

/** The district's own rigs, as the treatment table's bulk write reads them. */
const filingEquipmentOptions = computed(() =>
	store.filingEquipment.filter((equipment) => equipment.active),
);

async function annotateFiling(): Promise<void> {
	if (busy.value) return;
	const rig = store.filingEquipment.find(
		(equipment) => equipment.id === store.filingEquipmentID,
	);
	if (!rig) {
		actionError.value = "Choose an equipment first.";
		return;
	}
	if (
		!confirm(
			[
				`Record that ${targetPhrase()} were applied with ${rig.name}?`,
				"",
				"Each treatment gets its own filing capture naming this rig. The capture is what VectorSurv filing needs and what a treatment cannot be filed without; a correction is a newer capture, and the earlier one stays in the history.",
				"This writes the capture only — nothing is sent to VectorSurv from here. What is filed, when, and with what content is on the integration's synchronization page.",
			].join("\n"),
		)
	) {
		return;
	}
	busy.value = "filing";
	actionError.value = "";
	actionStatus.value = "";
	try {
		const annotated = await store.annotateTreatments(store.bulkSelection);
		actionStatus.value = `Recorded the filing capture on ${formatBigNumber(annotated)} ${tableLabel.value}.`;
	} catch {
		failure("The filing capture could not be recorded.");
	} finally {
		busy.value = null;
	}
}

onMounted(() => {
	if (symbologyCatalog.value === null) {
		void loadSymbologyCatalog().catch(() => {});
	}
	if (store.filingEquipment.length === 0) {
		void store.loadFilingEquipment();
	}
});
</script>

<template>
	<div class="selection-actions">
		<header class="sa-head">
			<h2 class="sa-title">
				{{ formatBigNumber(count) }} {{ tableLabel }} selected
			</h2>
			<p class="sa-scope">{{ scope }}</p>
		</header>

		<div v-if="actionStatus" class="sa-feedback sa-feedback--ok" role="status">
			{{ actionStatus }}
		</div>
		<div v-if="actionError" class="sa-feedback sa-feedback--error" role="alert">
			{{ actionError }}
		</div>

		<!-- Sites: the district's planning mark and its tag vocabulary. -->
		<template v-if="store.activeTable === 'site'">
			<section class="sa-card">
				<h3 class="sa-card-title">Assign a technician</h3>
				<p class="sa-help">
					Writes one planned assignment onto every ticked Site. Each Site is
					assigned to one person at a time, so this replaces the plan it has
					now; the earlier plan stays in the history. Clear cancels the current
					plan without deleting it.
				</p>
				<div class="sa-grid">
					<label class="sa-field">
						<span>Work</span>
						<select v-model="assignKind" class="form-select form-select-sm">
							<option value="inspect">Site inspection</option>
							<option value="treat">Treatment</option>
							<option value="set_trap">Set trap</option>
							<option value="collect_trap">Collect trap</option>
							<option value="abate">Abatement</option>
							<option value="custom">Custom</option>
						</select>
					</label>
					<label class="sa-field">
						<span>Technician</span>
						<select
							v-model="assignTechnician"
							class="form-select form-select-sm"
						>
							<option :value="null">Choose a technician…</option>
							<option
								v-for="option in technicianOptions"
								:key="option.value"
								:value="option.value"
							>
								{{ option.label }}
							</option>
						</select>
					</label>
					<label class="sa-field">
						<span>Day</span>
						<input
							v-model="assignDate"
							type="date"
							class="form-control form-control-sm"
						/>
					</label>
				</div>
				<div class="sa-actions">
					<ButtonLoading
						text="Assign selected Sites"
						icon="bi bi-person-check"
						variant="primary"
						:loading="busy === 'assign'"
						:disabled="busy !== null || assignTechnician === null"
						@click="assignSites"
					/>
					<button
						type="button"
						class="btn btn-sm btn-outline-secondary"
						:disabled="busy !== null"
						@click="clearAssignments"
					>
						<i class="bi bi-person-dash"></i> Clear assignment
					</button>
				</div>
			</section>

			<section class="sa-card">
				<h3 class="sa-card-title">Apply or remove tags</h3>
				<p class="sa-help">
					Applies a tag to, or withdraws one from, every ticked Site. A Site
					already carrying an applied tag is unchanged; a Site without a removed
					tag is unchanged. Both choices may be used together, and removals
					count before additions, so a Site at its 12-tag cap can swap one tag
					for another.
				</p>
				<div class="sa-grid">
					<label class="sa-field">
						<span>Apply</span>
						<select v-model="tagAdd" class="form-select form-select-sm">
							<option :value="null">No tag applied</option>
							<option
								v-for="tag in liveTags"
								:key="tag.tag_id"
								:value="tag.tag_id"
							>
								{{ tag.name }}
							</option>
						</select>
					</label>
					<label class="sa-field">
						<span>Remove</span>
						<select v-model="tagRemove" class="form-select form-select-sm">
							<option :value="null">No tag removed</option>
							<option
								v-for="tag in catalogTags"
								:key="tag.tag_id"
								:value="tag.tag_id"
							>
								{{ tag.name }}{{ tag.retired ? " (retired)" : "" }}
							</option>
						</select>
					</label>
				</div>
				<div class="sa-actions">
					<ButtonLoading
						text="Write tags"
						icon="bi bi-tags"
						variant="primary"
						:loading="busy === 'tags'"
						:disabled="busy !== null || (tagAdd === null && tagRemove === null)"
						@click="applyTags"
					/>
				</div>
			</section>
		</template>

		<!-- Features: the manual revisit pin. -->
		<section v-else-if="store.activeTable === 'feature'" class="sa-card">
			<h3 class="sa-card-title">Planned revisit</h3>
			<p class="sa-help">
				Sets the manual revisit date on every ticked Feature. It replaces any
				date already set on them; Clear removes it. The date is independent of
				the treatment window the Feature's band is computed from.
			</p>
			<div class="sa-grid">
				<label class="sa-field">
					<span>Revisit date</span>
					<input
						v-model="revisitDraft"
						type="date"
						class="form-control form-control-sm"
					/>
				</label>
			</div>
			<div class="sa-actions">
				<ButtonLoading
					text="Set revisit date"
					icon="bi bi-pin-angle"
					variant="primary"
					:loading="busy === 'revisit'"
					:disabled="busy !== null || revisitDraft === ''"
					@click="setRevisit"
				/>
				<button
					type="button"
					class="btn btn-sm btn-outline-secondary"
					:disabled="busy !== null"
					@click="clearRevisit"
				>
					<i class="bi bi-pin-angle-fill"></i> Clear revisit date
				</button>
			</div>
		</section>

		<!-- Treatments: the filing capture VectorSurv requires. -->
		<section
			v-else-if="store.activeTable === 'treatment'"
			class="sa-card"
		>
			<h3 class="sa-card-title">VectorSurv filing capture</h3>
			<p class="sa-help">
				Records which rig applied every ticked treatment. VectorSurv refuses an
				application without equipment and an applicator, so this is what makes a
				treatment fileable; a treatment with no capture is reported as blocked
				and is never filed. The applicator stays the treatment's own creator
				unless a single treatment overrides it.
			</p>
			<div class="sa-grid">
				<label class="sa-field">
					<span>Equipment</span>
					<select
						class="form-select form-select-sm"
						:value="store.filingEquipmentID"
						:disabled="busy !== null || store.filingLoading"
						@change="
							store.setFilingEquipment(
								Number(($event.target as HTMLSelectElement).value) || null,
							)
						"
					>
						<option :value="null">Choose an equipment</option>
						<option
							v-for="equipment in filingEquipmentOptions"
							:key="equipment.id"
							:value="equipment.id"
						>
							{{ equipment.name }}
						</option>
					</select>
				</label>
				<p v-if="store.filingEquipmentError" class="sa-help">
					{{ store.filingEquipmentError }}
				</p>
				<p
					v-else-if="!store.filingLoading && filingEquipmentOptions.length === 0"
					class="sa-help"
				>
					This district has no equipment yet. Add one under Configuration →
					VectorSurv integration before recording a capture.
				</p>
			</div>
			<div class="sa-actions">
				<ButtonLoading
					text="Record filing capture"
					icon="bi bi-clipboard-check"
					variant="primary"
					:loading="busy === 'filing'"
					:disabled="busy !== null || store.filingEquipmentID === null"
					@click="annotateFiling"
				/>
			</div>
		</section>

		<p v-else class="sa-help">
			{{ tableLabel }} have no bulk actions yet. A selection still shows what it
			covers; clearing the ticks or switching tables returns this panel to the
			record details.
		</p>
	</div>
</template>

<style scoped>
.selection-actions {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.sa-head {
	padding-bottom: 8px;
	border-bottom: 1px solid #dee2e6;
}

.sa-title {
	margin: 0;
	font-size: 0.95rem;
	font-weight: 600;
}

.sa-scope {
	margin: 2px 0 0;
	font-size: 0.75rem;
	line-height: 1.35;
	color: #6c757d;
}

.sa-feedback {
	padding: 6px 8px;
	border-radius: 4px;
	font-size: 0.75rem;
	line-height: 1.35;
}

.sa-feedback--ok {
	background: #e8f5ee;
	color: #146c43;
}

.sa-feedback--error {
	background: #fdecea;
	color: #b3261e;
}

.sa-card {
	padding: 10px;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	background: #fff;
}

.sa-card-title {
	margin: 0 0 4px;
	font-size: 0.85rem;
	font-weight: 600;
}

.sa-help {
	margin: 0 0 8px;
	font-size: 0.72rem;
	line-height: 1.4;
	color: #6c757d;
}

.sa-grid {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.sa-field {
	display: flex;
	flex-direction: column;
	gap: 2px;
	font-size: 0.72rem;
	color: #495057;
}

.sa-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-top: 8px;
}
</style>
